import authService from '../../api/auth';
import {loading, handleError, storeLocal, destroyLocal, getLocal} from '@/state/generators/helpers';
import {abilitiesFor, rolePrecedence, landingPages, otherAppUrl} from '@/config/abilities';
import {acquireToken as acquireHealthToken, clearToken as clearHealthToken} from '@/api/health';
import appConfig from '@/config/app';

export const state = {
    user: getLocal('user') || null,
    authenticated: !!localStorage.getItem('accessToken'),
    errors: {},
    loading: false,
    service: authService('auth'),
}

export const mutations = {
    'SET_AUTHENTICATED': function (state, boolean) {
        state.authenticated = boolean
    },

    'SET_USER': function (state, user) {
        state.user = user;
        user ? storeLocal('user', user) : destroyLocal('user');
    },

    'SET_LOADING': function (state, boolean = true) {
        state.loading = boolean
    },

    'SET_ERRORS': function (state, errors) {
        state.errors = errors
    },
}

export const getters = {
    loading: function (state) {
        return !!state.loading
    },

    authenticated: function (state) {
        return !!state.authenticated
    },

    user: function (state) {
        return state.user ? JSON.parse(JSON.stringify(state.user)) : null
    },

    states: function (state) {
        const states = {};

        Object.keys(state.errors || {}).forEach(key => {
            states[key] = false
        })

        return states
    },

    errors: function (state) {
        const errors = Object.assign({}, (state.errors || {}))

        Object.keys(errors).forEach(key => {
            errors[key] = Array.isArray(errors[key]) ? errors[key].join(' ') : String(errors[key])
        })

        return errors;
    },

    roles: function (state) {
        return state.user?.roles || []
    },

    hasRole: (state) => (role) => {
        return (state.user?.roles || []).includes(role)
    },

    primaryRole: function (state) {
        const roles = state.user?.roles || [];

        return rolePrecedence.find(role => roles.includes(role)) || null
    },

    can: (state) => (ability) => {
        if (ability === true || ability === undefined || ability === null) {
            return true;
        }

        return abilitiesFor(state.user?.roles || []).includes(ability);
    },

    landing: function (state, getters) {
        return landingPages[getters.primaryRole] || null
    },

    belongsElsewhere: function (state, getters) {
        return getters.authenticated && !landingPages[getters.primaryRole]
    },

    otherAppUrl: function () {
        return otherAppUrl
    },
}

export const actions = {
    login: function ({state, commit}, credentials) {
        loading(commit)
        commit('SET_ERRORS', {});

        localStorage.removeItem('accessToken');

        return state.service.login(credentials).then((response) => {
            if (response?.token) {
                localStorage.setItem('accessToken', response.token);
            }

            commit('SET_USER', response?.user || null);
            commit('SET_AUTHENTICATED', true);
            storeLocal('authenticated', true);

            const finish = () => {
                loading(commit, false);

                return response;
            };

            if ((response?.user?.roles || []).includes('patient') && credentials?.password) {
                return acquireHealthToken(credentials).catch(() => {
                }).then(finish);
            }

            return finish();
        }).catch((e) => {
            handleError(e, commit);
            throw e;
        });
    },

    switchDemoAccount: function ({dispatch}, role) {
        const account = appConfig.demoAccounts.find(entry => entry.role === role);

        if (!account) {
            return Promise.reject(new Error(`Unknown demo role: ${role}`));
        }

        return dispatch('logout').then(() => dispatch('login', {email: account.email, password: account.password}));
    },

    me: function ({state, commit, dispatch}) {
        return state.service.me().then((user) => {
            commit('SET_USER', user || null);
            commit('SET_AUTHENTICATED', true);

            return user;
        }).catch((e) => {
            if (e?.response?.status === 401) {
                dispatch('logoutOnlyFrontend');
            }
        });
    },

    logout: function ({state, commit, dispatch}) {
        loading(commit);

        return state.service.logout().catch(() => {
        }).then(() => {
            commit('SET_AUTHENTICATED', false);
            dispatch('logoutOnlyFrontend');
            loading(commit, false);
        });
    },

    logoutOnlyFrontend: function ({commit}) {
        destroyLocal('authenticated');
        destroyLocal('user');
        localStorage.removeItem('accessToken');
        clearHealthToken();
        this.reset();
        commit('SET_AUTHENTICATED', false);
        commit('SET_USER', null);
    },

    addError: function ({commit, state}, payload) {
        commit('SET_ERRORS', Object.assign({}, state.errors, payload))
    },
}
